import { Person, Groups2Outlined } from '@mui/icons-material';
import { Box, CircularProgress, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Link } from 'react-router-dom';
import { Member } from '../../../Models/Member/Member.model';
import { MemberStatuses } from '../../../utils/mappings';
import IProviderSelectOption from '../../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../../Models/Role/Role.model';
import { RootState } from '../../../reducers';
import { apiString } from '../../../utils/constants';
import RoleWithAlternates from '../RoleWithAlternates';
import { setExpandedRoleIndex } from '../../../store/careTeams.slice';
import SelectAlternateProviderDialog from '../SelectAlternateProviderDialog';
import TeamMessages from './TeamMessages';
import TeamAiSummary from './TeamAiSummary/TeamAiSummary';
import Authorization from '../../../utils/Authorization';

export interface MyCareTeamDetailsProps {
    roles: IRole[];
    providerOptions: IProviderSelectOption[];
}

const MyCareTeamDetails: React.FC<MyCareTeamDetailsProps> = ({ roles, providerOptions }) => {
    const { selectedTeam, expandedRoleIndex } = useSelector((state: RootState) => state.careTeams);
    const [assignedMembers, setAssignedMembers] = useState<Member[]>([]);
    const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [selectedMember, setSelectedMember] = useState<Member | null>(null);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const dispatch = useAppDispatch();
    const [authData, setAuthData] = useState<Authorization | null>(null);

    useEffect(() => {
        const auth = new Authorization();
        if (!authData) {
            setAuthData(auth);
        }
    }, [authData]);

    useEffect(() => {
        const fetchAssignedMembers = async () => {
            setLoadingMembers(true);
            try {
                const response = await axios.post(`${apiString}/member/getmembersbyids`, selectedTeam?.AssignedMemberIds || []);
                if (response.status === 200) {
                    setAssignedMembers(response.data);
                }
            } catch (error) {
                console.error('Error fetching assigned members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };
        if (selectedTeam?.AssignedMemberIds.length > 0) {
            fetchAssignedMembers();
        }
        // Reset pagination when team changes
        setPage(0);
    }, [selectedTeam?.AssignedMemberIds]);

    const handleOpenDialog = (member: Member) => {
        setSelectedMember(member);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedMember(null);
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'ahcccsId', headerName: 'AHCCCS ID', width: 150 },
        { field: 'dob', headerName: 'DOB', width: 150 },
        { field: 'status', headerName: 'Status', width: 180, sortable: true, filterable: true },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <>
                    <Tooltip title="View Member Chart">
                        <IconButton
                            component={Link}
                            to={`/provider/members-list/member-chart/${params.row.id}/member-profile`}
                            color="primary"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Person />
                        </IconButton>
                    </Tooltip>
                    {authData?.isInPermission('Manage teams') && (
                        <Tooltip title="Select Alternate Provider">
                            <IconButton color="secondary" onClick={() => handleOpenDialog(params.row)}>
                                <Groups2Outlined />
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            ),
        },
    ];

    const rows = assignedMembers.map((member) => ({
        ...member,
        id: member.Id,
        name: `${member.FirstName} ${member.LastName}`,
        ahcccsId: member.Mem_AHCCCS_ID,
        dob: new Date(member.DOB).toLocaleDateString(),
        status: MemberStatuses.find((s) => s.Id === member.Status)?.Name || member.Status,
    }));

    return (
        <Box>
            {selectedTeam?.Id && <TeamAiSummary teamId={selectedTeam.Id} />}
            <Paper variant="outlined" sx={{ padding: 2, marginBottom: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="h5" sx={{ marginBottom: 2 }}>
                            Team Address
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <TextField
                                    label="Address Line 1"
                                    value={selectedTeam?.MeetingAddress?.Address1}
                                    InputProps={{ readOnly: true, style: { color: 'black' } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    label="Address Line 2"
                                    value={selectedTeam?.MeetingAddress?.Address2}
                                    InputProps={{ readOnly: true, style: { color: 'black' } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    label="City"
                                    value={selectedTeam?.MeetingAddress?.City}
                                    InputProps={{ readOnly: true, style: { color: 'black' } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    label="State"
                                    value={selectedTeam?.MeetingAddress?.State}
                                    InputProps={{ readOnly: true, style: { color: 'black' } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    label="Zip Code"
                                    value={selectedTeam?.MeetingAddress?.ZipCode}
                                    InputProps={{ readOnly: true, style: { color: 'black' } }}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={12} container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="h5" sx={{ marginBottom: 2 }}>
                                Team Roster
                            </Typography>
                            <Grid container spacing={2}>
                                {[...(selectedTeam?.TeamRoles || [])]
                                    .sort((a, b) => {
                                        const roleA = roles.find((r) => r.Id === a.RoleId)?.Name || '';
                                        const roleB = roles.find((r) => r.Id === b.RoleId)?.Name || '';
                                        return roleA.localeCompare(roleB);
                                    })
                                    .map((role, index) => (
                                        <RoleWithAlternates
                                            key={index}
                                            role={role}
                                            roles={roles}
                                            providerOptions={providerOptions}
                                            assignedMembers={assignedMembers}
                                            expanded={expandedRoleIndex === index}
                                            onExpand={() => dispatch(setExpandedRoleIndex(expandedRoleIndex === index ? null : index))}
                                            teamRoleAlternates={selectedTeam?.TeamRoleAlternates || []}
                                        />
                                    ))}
                            </Grid>

                            <Typography variant="h5" sx={{ marginTop: 3, marginBottom: 2 }}>
                                Assigned Members
                            </Typography>
                            {loadingMembers ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box sx={{ height: 400, width: '100%' }}>
                                    <DataGrid
                                        getRowId={(row) => row.id}
                                        rows={rows}
                                        columns={columns}
                                        paginationModel={{ pageSize: pageSize, page: page }}
                                        onPaginationModelChange={(model) => {
                                            setPageSize(model.pageSize);
                                            setPage(model.page);
                                        }}
                                        pageSizeOptions={[5, 10, 25, 50]}
                                        disableRowSelectionOnClick
                                    />
                                </Box>
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TeamMessages teamId={selectedTeam?.Id} />
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            {selectedMember && (
                <SelectAlternateProviderDialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    member={selectedMember}
                    roles={roles}
                    providerOptions={providerOptions}
                    selectedTeam={selectedTeam}
                />
            )}
        </Box>
    );
};

export default MyCareTeamDetails;
